import axios from 'axios';

import { type Media, deleteMedia, postMedia, updateMedia } from './media';
import { Option } from '../types/option';

const BASE_URL = '/api/categories';
const PUBLICATIONS_URL = `${BASE_URL}/publications`;

export type CategoryId = number;
export type CategoryPublicationId = number;

export enum CategoryStatus {
    ARCHIVE = 0,
    ACTIVE = 1,
}

export enum CategoryPublicationStatus {
    DRAFT = 0,
    ONLINE = 1,
}

export type Category = {
    id: CategoryId;
    title: string;
    priority: number;
    status: number;
    createdAt: string;
    media: Option<Media>;
};

type CategoryPayload = {
    title: string;
    status: CategoryStatus;
};

type ReOrderCategoryPayload = {
    id: CategoryId;
    priority: number;
};

export type CategoryPublication = {
    id: CategoryPublicationId;
    title: string;
    content: string;
    link: string;
    status: CategoryPublicationStatus;
    medias: Media[];
};

type CategoryPublicationPayload = {
    title: string;
    content: string;
    link: string;
    status: CategoryPublicationStatus;
};

export async function getCategoryMedia(categoryId: CategoryId): Promise<Option<Media>> {
    try {
        const { data } = await axios.get<Media>(`${BASE_URL}/${categoryId}/medias`);
        return Option.some(data);
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return Option.none();
        }
        throw error;
    }
}

export async function postCategoryMedia(categoryId: CategoryId, file: File): Promise<void> {
    await postMedia(`${BASE_URL}/${categoryId}/medias`, file, 0);
}

export async function getCategoryPublicationMedias(
    publicationId: CategoryPublicationId,
): Promise<Media[]> {
    const { data } = await axios.get<Media[]>(`${PUBLICATIONS_URL}/${publicationId}/medias`);
    return data;
}

export async function postCategoryPublicationMedia(
    publicationId: CategoryPublicationId,
    file: File,
    priority: number,
): Promise<void> {
    await postMedia(`${PUBLICATIONS_URL}/${publicationId}/medias`, file, priority);
}

export type CategoryPublicationMediaSyncItem = {
    id: Option<number>;
    file: Option<File>;
    uploadSequence: number;
};

export async function syncCategoryPublicationMedias(
    publicationId: CategoryPublicationId,
    items: CategoryPublicationMediaSyncItem[],
    mediaIdsToDelete: ReadonlyArray<number> = [],
): Promise<void> {
    const sorted = [...items].sort((a, b) => a.uploadSequence - b.uploadSequence);
    const uploadPromises = sorted.map(item =>
        item.file
            .map(file =>
                item.id.match(
                    () => postCategoryPublicationMedia(publicationId, file, item.uploadSequence),
                    id => updateMedia(id, file, item.uploadSequence),
                ),
            )
            .getOrElse(Promise.resolve()),
    );
    const deletePromises = mediaIdsToDelete.map(mediaId => deleteMedia(mediaId));

    await Promise.all([...uploadPromises, ...deletePromises]);
}

export async function enrichCategoryPublicationWithMedias(
    publication: CategoryPublication,
): Promise<CategoryPublication> {
    if (publication.medias.length > 0) {
        return publication;
    }

    const medias = await getCategoryPublicationMedias(publication.id);
    return { ...publication, medias };
}

export async function getCategories(
    search: Option<string> = Option.none(),
    status: Option<CategoryStatus> = Option.none(),
): Promise<Category[]> {
    const { data } = await axios.get<Omit<Category, 'media'>[]>(BASE_URL, {
        params: {
            search: search.toNull(),
            status: status.toNull(),
        },
    });

    return Promise.all(
        data.map(async category => ({
            ...category,
            media: await getCategoryMedia(category.id),
        })),
    );
}

export async function getCategoriesPublications(
    categoryId: CategoryId,
): Promise<CategoryPublication[]> {
    const { data } = await axios.get<Omit<CategoryPublication, 'medias'>[]>(
        `${BASE_URL}/${categoryId}/publications`,
    );

    return data.map(publication => ({
        ...publication,
        medias: [],
    }));
}

export async function createCategoryPublication(
    categoryId: CategoryId,
    payload: CategoryPublicationPayload,
): Promise<CategoryPublication> {
    const { data } = await axios.post<CategoryPublication>(
        `${BASE_URL}/${categoryId}/publications`,
        payload,
    );
    return data;
}

export async function updateCategoryPublication(
    publicationId: CategoryPublicationId,
    payload: CategoryPublicationPayload,
): Promise<void> {
    await axios.put(`${PUBLICATIONS_URL}/${publicationId}`, payload);
}

export async function deleteCategoryPublication(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.delete(`${PUBLICATIONS_URL}/${publicationId}`);
}

export async function createCategory(payload: CategoryPayload): Promise<Category> {
    const { data } = await axios.post<Omit<Category, 'media'>>(BASE_URL, payload);
    return {
        ...data,
        media: Option.none(),
    };
}

export async function updateCategory(
    categoryId: CategoryId,
    payload: CategoryPayload,
): Promise<void> {
    await axios.put(`${BASE_URL}/${categoryId}`, payload);
}

export async function reOrderCategory(payload: ReOrderCategoryPayload[]): Promise<void> {
    await axios.post(`${BASE_URL}:order`, payload);
}

export async function trackCategoryPublicationVisited(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.post(`${PUBLICATIONS_URL}/${publicationId}:visited`);
}

export async function trackCategoryPublicationCreated(
    publicationId: CategoryPublicationId,
): Promise<void> {
    await axios.post(`${PUBLICATIONS_URL}/${publicationId}:created`);
}
